import type { AssetTypeDetail } from "@/entities/asset-type";
import type { ComponentTreeTranslation } from "@/entities/component-tree";
import type { DefectTree } from "@/entities/defect-tree";
import type { Nullable } from "@/types";

import { useEffect, useState } from "react";

import { extractDefectTreeComponentsKeys } from "@/components/AssetType/AssetTypeFormHelpers";
import { useReferentialLanguages } from "@/entities/referential/referential-languages";
import { useUpdate } from "@/hooks/queries";
import { useApiErrorHandler } from "@/modules/api-errors";
import { useSaveBar } from "@/modules/app-save-bar";
import { removeEmptyValuesFromObject } from "@/utilities/object";

export const useAssetTypeSeverityGrid = (assetType?: AssetTypeDetail) => {
  const mutation = useUpdate("assetTypes", assetType?.id ?? 0);
  const { setIsDirty } = useSaveBar();
  const { onError } = useApiErrorHandler();
  const initDefectTree = assetType?.defect_tree;
  const initTranslation = assetType?.component_tree_translation;

  const { languages } = useReferentialLanguages();
  const [defectTree, setDefectTree] = useState<Nullable<DefectTree>>();
  const [translation, setTranslation] =
    useState<Nullable<ComponentTreeTranslation | undefined>>(initTranslation);

  useEffect(() => {
    setDefectTree(initDefectTree);
  }, [initDefectTree]);

  useEffect(() => {
    setTranslation(initTranslation);
  }, [initTranslation]);

  const onUpdateDefectTree = (
    modifiedDefectTree: string,
    isRegistrable: boolean
  ) => {
    try {
      const json = JSON.parse(modifiedDefectTree);
      setDefectTree(json);
      cleanUpTranslations(json);
      if (isRegistrable) setIsDirty(true);
    } catch (_exception) {
      // No need to update defect tree if json is not valid
    }
  };

  const onUpdateTranslation = (
    modifiedTranslation: string,
    isRegistrable: boolean
  ) => {
    try {
      const json = JSON.parse(modifiedTranslation);
      setTranslation(json);
      if (isRegistrable) setIsDirty(true);
    } catch (_exception) {
      // No need to update translation if json is not valid
    }
  };

  const cleanUpTranslations = (tree: DefectTree) => {
    const nonEmptyTranslations = removeEmptyValuesFromObject(
      translation ?? {}
    ) as Record<string, Record<string, string>>;

    const keys = extractDefectTreeComponentsKeys(tree);

    const output = languages.map(({ code }) => {
      return [
        code,
        Object.assign(
          {},
          Object.fromEntries(keys.map((key: string) => [key, ""])),
          nonEmptyTranslations[code] ?? {}
        ),
      ];
    });

    const newTranslations = Object.fromEntries(output);

    setTranslation(newTranslations);
  };

  const handleSave = () => {
    const isDefectTreeEmpty = Object.keys(defectTree ?? {}).length === 0;

    const data = {
      defect_tree: isDefectTreeEmpty ? null : defectTree,
      component_tree_translation: translation,
    };

    mutation.mutate(data, {
      onSuccess: function () {
        setIsDirty(false);
      },
      onError,
    });
  };

  const handleCancel = () => {
    setDefectTree(initDefectTree);
    setTranslation(initTranslation);

    setIsDirty(false);
  };

  return {
    defectTree,
    translation,
    onUpdateDefectTree,
    onUpdateTranslation,
    handleSave,
    handleCancel,
    lastUpdatedAt: assetType?.updated_at,
    loading: mutation.isPending,
  };
};
